// frontend/src/components/common/Layout.jsx - Fixed scrolling version
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Sidebar from '@components/sidebar/Sidebar'
import { Menu, X, ChevronLeft, ChevronRight, Bot } from 'lucide-react'

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true)
      }
    }

    // Set initial state
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  return (
    // MAIN FIX: Remove h-screen constraint and allow natural height
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {(sidebarOpen || isMobile) && (
          <motion.div
            initial={false}
            animate={{
              x: isMobile ? (sidebarOpen ? 0 : '-100%') : 0,
            }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={`${isMobile ? 'fixed' : 'relative'} inset-y-0 left-0 z-30 w-80 lg:z-0`}
          >
            <Sidebar onClose={() => setSidebarOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sidebar Strip - shows when sidebar is closed on desktop */}
      {!isMobile && !sidebarOpen && (
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -50, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="relative w-12 bg-white border-r border-gray-200 shadow-sm flex flex-col items-center py-4 z-20"
        >
          {/* Toggle Button */}
          <motion.button
            whileHover={{ scale: 1.1, backgroundColor: '#f3f4f6' }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleSidebar}
            className="flex items-center justify-center w-8 h-8 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-md transition-all duration-200 shadow-sm hover:shadow-md mb-4"
            title="Open Sidebar"
          >
            <ChevronRight className="h-4 w-4 text-gray-700" />
          </motion.button>

          {/* App Icon */}
          <motion.div
            whileHover={{ scale: 1.1 }}
            className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-md mb-4"
          >
            <Bot className="h-5 w-5 text-white" />
          </motion.div>

          {/* Quick Action Dots */}
          <div className="flex flex-col space-y-3">
            <motion.div
              whileHover={{ scale: 1.2 }}
              className="w-2 h-2 bg-blue-400 rounded-full cursor-pointer"
              title="Dashboard"
              onClick={() => toggleSidebar()}
            />
            <motion.div
              whileHover={{ scale: 1.2 }}
              className="w-2 h-2 bg-green-400 rounded-full cursor-pointer"
              title="Test Suites"
              onClick={() => toggleSidebar()}
            />
            <motion.div
              whileHover={{ scale: 1.2 }}
              className="w-2 h-2 bg-purple-400 rounded-full cursor-pointer"
              title="Documentation"
              onClick={() => toggleSidebar()}
            />
            <motion.div
              whileHover={{ scale: 1.2 }}
              className="w-2 h-2 bg-gray-400 rounded-full cursor-pointer"
              title="Settings"
              onClick={() => toggleSidebar()}
            />
          </div>
        </motion.div>
      )}

      {/* Main content area - CRITICAL FIX: Remove flex flex-col and height constraints */}
      <motion.div 
        className="flex-1 min-w-0"
        layout
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      >
        {/* Header - shown on both mobile and desktop */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            {/* Toggle Button - only show if sidebar is open OR on mobile */}
            {(sidebarOpen || isMobile) && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleSidebar}
                className="flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {isMobile ? (
                  sidebarOpen ? <X className="h-4 w-4 text-gray-700" /> : <Menu className="h-4 w-4 text-gray-700" />
                ) : (
                  sidebarOpen ? <ChevronLeft className="h-4 w-4 text-gray-700" /> : <ChevronRight className="h-4 w-4 text-gray-700" />
                )}
              </motion.button>
            )}
            
            {/* Spacer when button is hidden */}
            {!sidebarOpen && !isMobile && <div className="w-8" />}
            
            <h1 className="text-lg font-semibold text-gray-900">
              Testing
            </h1>
            <div className="w-8" /> {/* Spacer for centering */}
          </div>
        </div>

        {/* Main content - CRITICAL FIX: Remove overflow-hidden and flex-1 constraints */}
        <main className="w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            {children}
          </motion.div>
        </main>
      </motion.div>
    </div>
  )
}

export default Layout